import React from 'react'
import {RouteComponentProps} from 'react-router-dom'
import gql from 'graphql-tag'
import {useQuery} from '@apollo/react-hooks'

import Heading from 'components/shared/Heading'
import DeliveredPhotos from 'components/shared/DeliveredPhotos'
import DeliveredUrl from 'components/shared/DeliveredUrl'
import DeliveredFloorPlans from 'components/shared/DeliveredFloorPlans'

import Loader from 'shared/components/Loader'
import PageNotFound from 'shared/components/PageNotFound'
import './index.scss'

type MatchParams = { sharingId: string }

const sharingQuery = gql`
  query SharingQuery($id: ID!) {
    sharing(id: $id) {
      id
      messageForRecipient
      includeNotPurchasedProducts
      estate {
        streetAddress
        broker {
          name
          email
          phone
        }
        segment {
          iconUrl
        }
      }
      products {
        edges {
          node {
            id
            name
            ...on PhotoBasedProduct {
              quantity
            }
            ...on AssetsBasedProduct {
              assets {
                edges {
                  node {
                    id
                    state
                    ...on PhotoAsset {
                      image(size: "monitor") {
                        url
                        thumbUrl
                        width
                        height
                        labels
                        filenameIndex
                      }
                    }
                    ...on DocumentAsset {
                      url
                      scope
                    }
                    ...on UrlAsset {
                      url
                    }
                    ...on PlanDrawingAsset {
                      free
                      assets {
                        edges {
                          node {
                            id
                            ...on PhotoAsset {
                              image(size: "monitor") {
                                url
                                thumbUrl
                                width
                                height
                                labels
                                filenameIndex
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`

const Header = ({sharing}: any) => {
  return (
    <header className="sharing-header">
      <Heading>{sharing.estate.streetAddress}</Heading>
      <div className="broker-info">
        <span><span>Megler:</span>{sharing.estate.broker.name}</span>
        <span><span>E-post:</span>{sharing.estate.broker.email}</span>
        <span><span>Telefon:</span>{sharing.estate.broker.phone}</span>
      </div>
    </header>
  )
}

const WithSharing = ({sharing}: any) => {
  const productDetails = (product: any) => {
    const photoProducts = [
      'AreaPhotoProduct',
      'CollageProduct',
      'DigitalRetouchedFurnishingPhotoProduct',
      'DigitalRetouchedRenovationPhotoProduct',
      'DigitalRetouchedPeoplePhotoProduct',
      'DronePhotoProduct',
      'ElevatedPhotoProduct',
      'EveningPhotoProduct',
      'FacadePhotoProduct',
      'PanoramaPhotoProduct',
      'PremiumPhotoProduct',
      'RetouchedEveningPhotoProduct',
      'StandardPhotoProduct',
    ]

    const urlProducts = [
      'EnergyLabelingProduct',
      'SlidesProduct',
      'VideoProduct',
    ]

    if (photoProducts.includes(product.__typename)) {
      return (
        <DeliveredPhotos
          product={product}
          estateId={sharing.estate.id}
          readOnly={true}
          photoOrder={undefined}
          canCreateCorrections={undefined}
        />
      )
    }

    if (urlProducts.includes(product.__typename)) {
      return (
        <DeliveredUrl
          product={product}
        />
      )
    }

    if (product.__typename === 'StylingProduct') {
      return (
        <DeliveredUrl
          product={product}
          getDeliveredUrl={({assets}) => {
            const report = assets.edges.find((e: any) => e.node.scope === 'output')
            return report && report.node.url
          }}
        />
      )
    }

    if (product.__typename === 'FloorPlansProduct') {
      return (
        <DeliveredFloorPlans
          product={product}
          estateId={sharing.estate.id}
          readOnly={true}
        />
      )
    }

    return null
  }

  return (
    <>
      <Header sharing={sharing} />

      <section className="message">
        <span>Melding:</span>
        {sharing.messageForRecipient}
      </section>

      <section className="sharing-content">
        {sharing.products.edges.map(({node: product}: any) => {
          const productComponent = productDetails(product)
          if (productComponent) {
            return (
              <div key={product.id}>
                <Heading level={2}>{product.name} {product.quantity ? `(${product.quantity})` : ''}</Heading>
                <div className="sharing-product">
                  {productComponent}
                </div>
              </div>
            )
          }
          return <React.Fragment key={product.id} />
        })}
      </section>
    </>
  )
}

const Sharing = ({match}: RouteComponentProps<MatchParams>) => {
  const {loading, data, error} = useQuery(sharingQuery, {
    variables: {id: match.params.sharingId}
  })

  const sharing = data ? data.sharing : null

  return (
    <div className="public-sharing">
      <Loader loading={loading} error={error}>
        {!sharing && (
          <PageNotFound />
        )}
        {sharing && (
          <WithSharing
            sharing={sharing}
          />
        )}
      </Loader>
    </div>
  )
}

export default Sharing
